import { SCALAR_FIELD_TYPE_OPTIONS, type ScalarFieldType, type TableColumnRequest } from '../api';
import { emptyColumn } from '../hooks/useFieldDefinitionState';

interface TableColumnEditorProps {
  columns: ReadonlyArray<TableColumnRequest>;
  onChange: (next: TableColumnRequest[]) => void;
  disabled?: boolean;
}

/**
 * Sub-editor mounted inside a FieldRow when the field's type is "Table". Lets the user declare
 * the column schema for the repeating rows: each column has a Name, scalar Type, Required flag,
 * optional regex Pattern and optional Hint. Nested tables are not allowed (the type select only
 * exposes scalars). The reorder feature is intentionally omitted — column order is irrelevant
 * to the AI prompt sub-schema (columns are keyed by name).
 */
export function TableColumnEditor({
  columns,
  onChange,
  disabled = false,
}: TableColumnEditorProps): JSX.Element {
  const update = (index: number, patch: Partial<TableColumnRequest>): void => {
    const next = columns.map((c, i) => (i === index ? { ...c, ...patch } : c));
    onChange(next);
  };

  const remove = (index: number): void => {
    onChange(columns.filter((_, i) => i !== index));
  };

  const add = (): void => {
    onChange([...columns, emptyColumn()]);
  };

  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50/60 p-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-800">Columnas de la tabla</h4>
        <span className="text-xs text-slate-500">
          {columns.length} columna{columns.length === 1 ? '' : 's'}
        </span>
      </div>
      {columns.length === 0 ? (
        <p className="mt-2 rounded-md border border-dashed border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
          Una tabla necesita al menos una columna. Añade la primera para empezar.
        </p>
      ) : (
        <div className="mt-2 space-y-2">
          {columns.map((col, index) => (
            <div
              key={index}
              className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 bg-white p-3 md:grid-cols-12"
            >
              <div className="md:col-span-3">
                <label className="label" htmlFor={`col-name-${index}`}>
                  Nombre
                </label>
                <input
                  id={`col-name-${index}`}
                  className="input text-sm"
                  type="text"
                  value={col.name}
                  placeholder="ej. cantidad"
                  onChange={(e) => update(index, { name: e.target.value })}
                  disabled={disabled}
                />
              </div>
              <div className="md:col-span-2">
                <label className="label" htmlFor={`col-type-${index}`}>
                  Tipo
                </label>
                <select
                  id={`col-type-${index}`}
                  className="input text-sm"
                  value={col.type}
                  onChange={(e) => update(index, { type: e.target.value as ScalarFieldType })}
                  disabled={disabled}
                >
                  {SCALAR_FIELD_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end md:col-span-2">
                <label className="flex items-center gap-1.5 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={col.required}
                    onChange={(e) => update(index, { required: e.target.checked })}
                    disabled={disabled}
                  />
                  Obligatorio
                </label>
              </div>
              <div className="md:col-span-3">
                <label className="label" htmlFor={`col-pattern-${index}`}>
                  Regex
                </label>
                <input
                  id={`col-pattern-${index}`}
                  className="input font-mono text-xs"
                  type="text"
                  value={col.pattern ?? ''}
                  placeholder="opcional"
                  onChange={(e) =>
                    update(index, { pattern: e.target.value === '' ? null : e.target.value })
                  }
                  disabled={disabled}
                />
              </div>
              <div className="flex items-end justify-end md:col-span-2">
                <button
                  type="button"
                  className="btn-secondary text-xs text-rose-700 hover:bg-rose-50"
                  onClick={() => remove(index)}
                  disabled={disabled}
                >
                  Eliminar
                </button>
              </div>
              <div className="md:col-span-12">
                <label className="label" htmlFor={`col-hint-${index}`}>
                  Pista de columna (opcional)
                </label>
                <input
                  id={`col-hint-${index}`}
                  className="input text-xs"
                  type="text"
                  maxLength={1000}
                  value={col.hint ?? ''}
                  placeholder='ej. "este precio incluye IVA"'
                  onChange={(e) =>
                    update(index, { hint: e.target.value === '' ? null : e.target.value })
                  }
                  disabled={disabled}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        className="btn-secondary mt-3 text-xs"
        onClick={add}
        disabled={disabled}
      >
        + Añadir columna
      </button>
    </div>
  );
}
